import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Building, Heart, ExternalLink, Bed, Bath, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import { toast } from 'sonner';

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  
  const [property, setProperty] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProperty();
    if (isAuthenticated) {
      checkIfSaved();
    }
  }, [propertyId, isAuthenticated]);

  const loadProperty = async () => {
    try {
      const response = await api.getProperty(propertyId);
      setProperty(response.data);
    } catch (error) {
      console.error('Error loading property:', error);
      toast.error('Property not found');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const checkIfSaved = async () => {
    try {
      const response = await api.getSavedProperties(getAuthHeader());
      setIsSaved(response.data.some(p => p.property_id === propertyId));
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  };

  const handleSaveToggle = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save properties');
      return;
    }

    try {
      if (isSaved) {
        await api.removeSavedProperty(propertyId, getAuthHeader());
        toast.success('Property removed from saved');
        setIsSaved(false);
      } else {
        await api.saveProperty(propertyId, getAuthHeader());
        toast.success('Property saved!');
        setIsSaved(true);
      }
    } catch (error) {
      toast.error('Failed to update saved status');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Loading property details...</p>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  return (
    <div className="min-h-screen py-8" data-testid="property-detail-page">
      <div className="container mx-auto px-6 md:px-12">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6"
          data-testid="back-btn"
        >
          ← Back to Properties
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-slate-200 h-96 rounded-sm mb-6 flex items-center justify-center">
              {property.images && property.images.length > 0 ? (
                <img 
                  src={property.images[0]} 
                  alt={property.title}
                  className="w-full h-full object-cover rounded-sm"
                />
              ) : (
                <Building className="h-24 w-24 text-slate-400" />
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded mb-2">
                    {property.category}
                  </span>
                  <h1 className="text-3xl md:text-4xl font-bold font-heading mb-2">
                    {property.title}
                  </h1>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleSaveToggle}
                  data-testid="save-property-detail-btn"
                >
                  <Heart 
                    className={`h-5 w-5 ${isSaved ? 'fill-red-500 text-red-500' : ''}`} 
                  />
                </Button>
              </div>

              <p className="text-4xl font-mono font-bold text-emerald-600 mb-6">
                £{property.price?.toLocaleString()}
              </p>

              {property.guide_price && (
                <p className="text-sm text-slate-600 mb-4">
                  Guide Price: {property.guide_price}
                </p>
              )}

              <div className="border-t border-b border-slate-200 py-4 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {property.bedrooms && (
                    <div className="flex items-center gap-2">
                      <Bed className="h-5 w-5 text-slate-600" />
                      <span className="text-sm">{property.bedrooms} Beds</span>
                    </div>
                  )}
                  {property.bathrooms && (
                    <div className="flex items-center gap-2">
                      <Bath className="h-5 w-5 text-slate-600" />
                      <span className="text-sm">{property.bathrooms} Baths</span>
                    </div>
                  )}
                  {property.property_type && (
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-slate-600" />
                      <span className="text-sm">{property.property_type}</span>
                    </div>
                  )}
                  {property.tenure && (
                    <div className="text-sm">
                      <span className="font-medium">Tenure:</span> {property.tenure}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-3 font-heading">Description</h2>
                <p className="text-slate-700 leading-relaxed">{property.description}</p>
              </div>

              {property.property_url && (
                <Button 
                  onClick={() => window.open(property.property_url, '_blank')}
                  className="w-full md:w-auto"
                  data-testid="view-auction-site-btn"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View on Auction Site
                </Button>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-sm p-6 sticky top-24">
              <h3 className="text-xl font-semibold mb-4 font-heading">Auction Details</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-[#F5A623] mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Auction Date</p>
                    <p className="font-medium">{formatDate(property.auction_date)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Building className="h-5 w-5 text-[#F5A623] mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Auction House</p>
                    <p className="font-medium">{property.auction_house}</p>
                    {property.auction_house_url && (
                      <a 
                        href={property.auction_house_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Visit Website
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#F5A623] mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Location</p>
                    <p className="font-medium">{property.location}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs text-slate-500">
                  Property ID: {property.property_id}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;